import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Loader2, User, Phone, Mail, IndianRupee } from 'lucide-react';
import { billAPI } from '../services/api';

const PendingPaymentsModal = ({ open, onOpenChange }) => {
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [error, setError] = useState(null);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const response = await billAPI.getPendingPayments({
                pageSize: 50, // Show more in modal
                pageNumber: 0
            });

            if (response.data && response.data.content) {
                setPayments(response.data.content);
            } else if (Array.isArray(response.data)) {
                setPayments(response.data);
            }
            setError(null);
        } catch (err) {
            console.error('Error fetching pending payments details:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load pending payments. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchPayments();
        }
    }, [open]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                        <Badge variant="destructive" className="animate-pulse">Pending Payments</Badge>
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground">Fetching pending payment records...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-12 text-red-500">
                        {error}
                    </div>
                ) : payments.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        No pending payments found.
                    </div>
                ) : (
                    <div className="mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Customer Details</TableHead>
                                    <TableHead className="text-right">Pending Amount</TableHead>
                                    <TableHead className="text-center">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {payments.map((payment, index) => (
                                    <TableRow key={payment.id || index} className="hover:bg-muted/50 transition-colors">
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="font-semibold flex items-center gap-2">
                                                    <User className="h-3 w-3" /> {payment.customerName}
                                                    {payment.contactNumber && (
                                                        <span className="text-xs text-muted-foreground ml-1">
                                                            ({payment.contactNumber})
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right font-bold text-red-600">
                                            <div className="flex items-center justify-end gap-1">
                                                <IndianRupee className="h-3 w-3" />
                                                {payment.totalPendingAmount || payment.pendingAmount || 0}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                                                PENDING
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PendingPaymentsModal;
